import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if we have a stored session
    const storedUser = localStorage.getItem('chat_user');
    const storedToken = localStorage.getItem('chat_id_token');

    if (storedUser && storedToken) {
      try {
        const user = JSON.parse(storedUser);
        setCurrentUser(user);
      } catch {
        localStorage.removeItem('chat_user');
        localStorage.removeItem('chat_id_token');
      }
    }

    setLoading(false);
  }, []);

  /**
   * Login with a Firebase ID token.
   * The token is verified by the backend.
   */
  const loginWithToken = async (idToken) => {
    const result = await api.verifyAuth(idToken);

    if (!result.success) {
      throw new Error('Authentication failed');
    }

    const user = {
      uid: result.uid,
      displayName: result.display_name || 'User',
      email: result.email || '',
      photoURL: result.photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(result.display_name || 'User')}&background=1e293b&color=fff`,
    };

    // Store session
    localStorage.setItem('chat_user', JSON.stringify(user));
    localStorage.setItem('chat_id_token', idToken);

    setCurrentUser(user);

    return user;
  };

  /**
   * Mock login for local development (bypasses Firebase Auth).
   */
  const mockLogin = () => {
    const user = {
      uid: 'mock-user-123',
      displayName: 'Local Dev User',
      email: 'dev@localhost.com',
      photoURL: 'https://ui-avatars.com/api/?name=Dev+User&background=1e293b&color=fff',
    };

    localStorage.setItem('chat_user', JSON.stringify(user));
    localStorage.setItem('chat_id_token', 'mock-token');

    setCurrentUser(user);
  };

  const logout = () => {
    localStorage.removeItem('chat_user');
    localStorage.removeItem('chat_id_token');
    setCurrentUser(null);
    return Promise.resolve();
  };

  const value = {
    currentUser,
    logout,
    mockLogin,
    loginWithToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
